import { formatMoney } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';

class ProductPrice extends HTMLElement {
  constructor() {
    super();

    this.productForm = null;
    this.unitVariantPrice = null;
  }

  connectedCallback() {
    const scope = this.closest('product-card, [data-ref="product-section-content"], [data-ref="popup-content"]');
    this.productForm = scope?.querySelector('[data-ref="product-form"]');

    const initialUnitPrice = Number(this.querySelector('[data-ref="price"]')?.dataset.value);
    if (Number.isFinite(initialUnitPrice)) {
      this.unitVariantPrice = initialUnitPrice;
    }

    this.productForm?.addEventListener(ThemeEvents.formUpdated, this.updatePrice);
    this.productForm?.addEventListener(ThemeEvents.variantUpdated, this.updateVariantDetails);
  }

  disconnectedCallback() {
    this.productForm?.removeEventListener(ThemeEvents.formUpdated, this.updatePrice);
    this.productForm?.removeEventListener(ThemeEvents.variantUpdated, this.updateVariantDetails);
  }

  updatePrice = (event) => {
    const priceContainer = this.querySelector('[data-ref="price-container"]');
    if (!priceContainer) return;

    // Récupérer les prix depuis l'événement
    const { price, compare_at_price, difference_price } = event.detail.prices;

    // Mettre à jour le prix
    const priceElement = priceContainer.querySelector('[data-ref="price"]');
    if (priceElement) {
      priceElement.innerHTML = formatMoney(price);
      priceElement.dataset.value = String(price);
    }

    // Do not refresh the square-metre price from this event. During a variant
    // switch, Shopify can emit `formUpdated` before `variantUpdated`: the new
    // price would then be divided briefly by the previous variant's area.
    // `updateVariantDetails` updates the unit price and area together, which
    // keeps the displayed value atomic and avoids the alarming intermediate
    // price. Quantity-only updates deliberately leave the unit price unchanged.

    // Mettre à jour le prix barré
    const compareAtPriceElement = priceContainer.querySelector('[data-ref="compare-at-price"]');
    if (compareAtPriceElement) {
      compareAtPriceElement.innerHTML = compare_at_price > price ? formatMoney(compare_at_price) : '';
    }

    // Mettre à jour le badge de réduction
    const differencePriceElement = priceContainer.querySelector('[data-ref="difference-price-value"]');
    if (differencePriceElement) {
      const salesBadgeType = this.dataset.salesBadge;
      const showMinus = differencePriceElement.dataset.showMinus !== 'true';

      // Afficher le badge seulement s'il y a une réduction (compare_at_price > price)
      if (compare_at_price > price) {
        const prefix = showMinus ? '-' : '';
        if (salesBadgeType === 'percentage') {
          const percentage = Math.round(((compare_at_price - price) * 100) / compare_at_price);
          differencePriceElement.innerHTML = `${prefix}${percentage}%`;
        } else {
          differencePriceElement.innerHTML = `${prefix}${formatMoney(difference_price)}`;
        }
      } else {
        // Pas de réduction, vider le badge
        differencePriceElement.innerHTML = '';
      }
    }
  };

  updateUnitPrice = (event) => {
    const unitPriceElement = this.querySelector('[data-ref="unit-price"]');
    if (!unitPriceElement) return;

    const variant = event.detail.resource;
    if (!variant?.unit_price_measurement) {
      unitPriceElement.innerHTML = '';
      return;
    }

    const formattedPrice = formatMoney(variant.unit_price);
    const { reference_value, reference_unit } = variant.unit_price_measurement;
    const reference = reference_value !== 1 ? `${reference_value} ${reference_unit}` : reference_unit;
    unitPriceElement.innerHTML = `${formattedPrice} / ${reference}`;
  };

  updateVariantDetails = (event) => {
    this.updateUnitPrice(event);

    const variant = event.detail.resource;
    const unitVariantPrice = Number(variant?.price);
    if (Number.isFinite(unitVariantPrice)) {
      this.unitVariantPrice = unitVariantPrice;
    }

    const variantAreasElement = this.querySelector('[data-ref="panosora-variant-areas"]');
    let variantAreas = {};
    try {
      variantAreas = JSON.parse(variantAreasElement?.textContent || '{}');
    } catch {
      variantAreas = {};
    }

    const variantArea = Number(variantAreas[String(variant?.id)]);
    if (Number.isFinite(variantArea) && variantArea > 0) {
      this.setSurfaceArea(variantArea);
    } else {
      this.updatePricePerSquareMeter();
    }
  };

  updatePricePerSquareMeter = () => {
    const pricePerSquareMeterElement = this.querySelector('[data-ref="panosora-price-per-m2"]');
    if (!pricePerSquareMeterElement) return;

    const area = Number(pricePerSquareMeterElement.dataset.area);
    const unitPrice = Number(this.unitVariantPrice);
    if (!Number.isFinite(area) || area <= 0 || !Number.isFinite(unitPrice)) return;

    const roundedPricePerSquareMeter = Math.round(unitPrice / area / 100);
    const locale = document.documentElement.lang || 'fr-FR';
    const currency = window.Theme?.currency || 'EUR';
    const formattedPricePerSquareMeter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(roundedPricePerSquareMeter);

    pricePerSquareMeterElement.innerHTML = `(${formattedPricePerSquareMeter} / m²)`;
  };

  setSurfaceArea = (area) => {
    const pricePerSquareMeterElement = this.querySelector('[data-ref="panosora-price-per-m2"]');
    const areaElement = this.querySelector('[data-ref="panosora-unit-area"]');
    if (!pricePerSquareMeterElement || !areaElement) return;

    pricePerSquareMeterElement.dataset.area = String(area);
    const displayArea = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 }).format(area);
    areaElement.textContent = displayArea;

    this.updatePricePerSquareMeter();
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
